import React, { useState, Fragment } from 'react'
import { Sheet, BodyText } from '@hm-group/fabric-components'
import {
  DeliveryReturnsInfoLoader,
  HeadingSmall,
  HeadingMedium,
  FadeSwap,
  Panel,
  ActionTriggerBox,
} from '@pb/components'
import { useRequest, getLabelFromStorage, trackEvent } from '@pb/utils'
import cn from 'classnames'
import { trim } from 'ramda'

import { TRACKING_EVENTS } from 'App/constants'
import OPTIMIZELY from 'constants/optimizely'
import { trackOptimizelyEvent } from 'helpers/optimizely'

import { DELIVERY_AND_RETURNS_DATA_SOURCES } from './constants'
import styles from './DeliveryReturnsInfo.scss'
import {
  DeliveryReturnsInfoProps,
  HelpParagraphComponent,
  NormalizedSection,
  NormalizedSubSection,
} from './types'

const normalizeDeliveryReturnsInfo = ({
  sections = [],
}: HelpParagraphComponent): NormalizedSection[] =>
  sections.map((section) => ({
    heading: section.heading.trim(),
    subSections: section['sub-sections'].map((subSection) => ({
      subHeading: trim(subSection['sub-heading']),
      bulletList: subSection['bullet-list'],
      bodyTexts: subSection['body-texts'],
    })),
  }))

const DeliveryReturnsInfo: React.FC<DeliveryReturnsInfoProps> = ({
  shouldShowLoaders,
  dataSource,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const {
    loading: isRequestLoading,
    data: deliveryReturnsInfo,
    error,
    execute: fetchDeliveryReturnsInfo,
  } = useRequest('helpParagraphComponent', {
    type: 'adobe',
    use: { apiVersion: false },
    manual: true,
  }) as { loading: boolean; data: HelpParagraphComponent; error: unknown; execute: any }
  if (error) return null

  const trackActionTriggerBox = (): void => {
    trackOptimizelyEvent(OPTIMIZELY.SHOPPINGBAG_CLICK_DELIVERY_AND_RETURNS_INFO_CTA)
    trackEvent({
      ...TRACKING_EVENTS.deliveryAndReturnsInfoClick.data,
    })
  }

  const isCompressedSidebar = process.env.IS_CART_COMPRESSED_SIDEBAR_ENABLED === 'enabled'

  const isDrawerEnabled =
    (dataSource === DELIVERY_AND_RETURNS_DATA_SOURCES.cart &&
      process.env.IS_CART_DELIVERY_AND_RETURN_DRAWER_ENABLED === 'enabled') ||
    (dataSource === DELIVERY_AND_RETURNS_DATA_SOURCES.checkout &&
      process.env.IS_CHECKOUT_DELIVERY_AND_RETURN_DRAWER_ENABLED === 'enabled')

  const normalizedText =
    !!deliveryReturnsInfo &&
    normalizeDeliveryReturnsInfo(deliveryReturnsInfo).map(
      ({ heading, subSections }, sectionIndex) => {
        const renderBulletList = (subSection: NormalizedSubSection): JSX.Element | null => {
          if (!subSection.bulletList) {
            return null
          }

          return (
            <ul className={styles.bulletList}>
              {subSection.bulletList.map((item) => (
                <li key={item} className={styles.bulletItem}>
                  {item}
                </li>
              ))}
            </ul>
          )
        }

        const renderBodyTexts = (subSection: NormalizedSubSection): JSX.Element | null => {
          if (!subSection.bodyTexts) {
            return null
          }

          return (
            <div className={styles.bodyText}>
              {subSection.bodyTexts.map((item) => (
                <BodyText key={item} className={styles.bodyTextItem}>
                  {item}
                </BodyText>
              ))}
            </div>
          )
        }

        return (
          // eslint-disable-next-line
          <div key={sectionIndex} className={styles.wrapper}>
            {heading && <HeadingMedium className={styles.heading}>{heading}</HeadingMedium>}

            {subSections.map((subSection, subSectionIndex) => (
              // eslint-disable-next-line
              <Fragment key={subSectionIndex}>
                {subSection.subHeading && (
                  <HeadingSmall Tag="h3" className={styles.subHeading}>
                    {subSection.subHeading}
                  </HeadingSmall>
                )}

                {renderBodyTexts(subSection)}
                {renderBulletList(subSection)}
              </Fragment>
            ))}
          </div>
        )
      },
    )

  return (
    <FadeSwap
      flag={shouldShowLoaders}
      placeholder={
        !isDrawerEnabled && (
          <DeliveryReturnsInfoLoader isCompressedContainer={isCompressedSidebar} />
        )
      }
    >
      {isDrawerEnabled ? (
        <Panel className={cn(styles.container)}>
          <ActionTriggerBox
            title={getLabelFromStorage('cart.sidebar.delivery.heading')}
            icon="ParcelIcon"
            onClick={async () => {
              if (!deliveryReturnsInfo) await fetchDeliveryReturnsInfo()
              setIsOpen(!isOpen)
              trackActionTriggerBox()
            }}
            shouldShowSpinner={isRequestLoading}
          />
          <Sheet
            isOpen={isOpen}
            onClose={() => setIsOpen(!isOpen)}
            heading={getLabelFromStorage('cart.sidebar.delivery.heading')}
            closeLabel={getLabelFromStorage('general.close')}
            hasFixedMobileHeight
          >
            {normalizedText}
          </Sheet>
        </Panel>
      ) : (
        <Panel
          className={cn(
            styles.container,
            isCompressedSidebar ? styles.isCompressedContainer : styles.isNotCompressedContainer,
          )}
        >
          {normalizedText}
        </Panel>
      )}
    </FadeSwap>
  )
}

export default React.memo(DeliveryReturnsInfo)
